import { useEffect, useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import { Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { FaTrash, FaEdit, FaPlus, FaChevronRight } from 'react-icons/fa';
import { ButtonPrimary } from '../../components/Button/ButtonPrimary';
import style from './activity-log.module.scss';
import cx from 'classnames';

// --- Refactored Imports ---
import { ActivityLog } from '../../../models/ActivityLog/ActivityLog';
import {
  ActivityLogType,
  ActivityLogEvent
} from '../../../models/ActivityLog/contracts';
import { format, isToday, isYesterday, parseISO } from 'date-fns';

// --- Helper Dictionaries ---
const eventDisplayMap: Record<
  ActivityLogEvent,
  { name: string; icon: React.ComponentType; color: string }
> = {
  [ActivityLogEvent.CREATED]: {
    name: 'Kreirano',
    icon: FaPlus,
    color: 'green'
  },
  [ActivityLogEvent.UPDATED]: {
    name: 'Izmenjeno',
    icon: FaEdit,
    color: 'blue'
  },
  [ActivityLogEvent.DELETED]: { name: 'Obrisano', icon: FaTrash, color: 'red' }
};

const logNameDisplayMap: Record<ActivityLogType, string> = {
  [ActivityLogType.PRODUCT]: 'Proizvod',
  [ActivityLogType.CATEGORY]: 'Kategorija',
  [ActivityLogType.ORDER]: 'Narudžbina',
  [ActivityLogType.ORDER_DOCUMENT]: 'Dokument Narudžbine',
  [ActivityLogType.PROJECT]: 'Projekat',
  [ActivityLogType.USER]: 'Korisnik'
};

// --- Type for Table Rows ---
interface ActivityLogTableDataType {
  key: number;
  time: string;
  user: string;
  action: ActivityLogEvent;
  subject: string;
}

const ActivityLogPage = () => {
  const [activities, setActivities] = useState<ActivityLog[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [canLoadMore, setCanLoadMore] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // --- Data Fetching Logic ---
  const loadActivities = async (isInitialLoad = false) => {
    setIsLoading(true);
    try {
      const newActivities = await ActivityLog.get({
        skip: isInitialLoad ? 0 : activities.length,
        take: 50 // Load more items to have more to group
      });

      if (newActivities.length < 50) {
        setCanLoadMore(false);
      }

      setActivities((prev) =>
        isInitialLoad ? newActivities : [...prev, ...newActivities]
      );

      // Automatically expand the first group on initial load
      if (isInitialLoad && newActivities.length > 0) {
        const firstGroupKey = format(newActivities[0].createdAt, 'yyyy-MM-dd');
        setExpandedGroups(new Set([firstGroupKey]));
      }
    } catch (error) {
      if ((error as any).name !== 'CanceledError') {
        toast.error('Greška prilikom učitavanja istorije aktivnosti.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    loadActivities(true);

    return () => {
      controller.abort();
    };
  }, []);

  // --- Grouping Logic ---
  const groupedActivities = useMemo(() => {
    const groups: Record<string, ActivityLog[]> = {};
    activities.forEach((activity) => {
      const dateKey = format(activity.createdAt, 'yyyy-MM-dd');
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(activity);
    });
    return groups;
  }, [activities]);

  const toggleGroup = (key: string) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const getGroupTitle = (dateKey: string) => {
    const date = parseISO(dateKey);
    if (isToday(date)) return 'Danas';
    if (isYesterday(date)) return 'Juče';
    return format(date, 'd. MMMM yyyy.');
  };

  // --- Table Columns Definition ---
  const columns: ColumnsType<ActivityLogTableDataType> = [
    {
      title: 'Vreme',
      dataIndex: 'time',
      key: 'time',
      width: 100
    },
    {
      title: 'Korisnik',
      dataIndex: 'user',
      key: 'user',
      width: 150
    },
    {
      title: 'Akcija',
      dataIndex: 'action',
      key: 'action',
      render: (event: ActivityLogEvent) => {
        const eventInfo = eventDisplayMap[event] || {
          name: event,
          color: 'default'
        };
        return <Tag color={eventInfo.color}>{eventInfo.name}</Tag>;
      },
      width: 120
    },
    {
      title: 'Subjekat',
      dataIndex: 'subject',
      key: 'subject'
    }
  ];

  return (
    <div className={style.pageWrapper}>
      <h1 className="cs-title">Istorija Aktivnosti</h1>

      {Object.keys(groupedActivities).map((dateKey) => {
        const groupActivities = groupedActivities[dateKey];
        const isExpanded = expandedGroups.has(dateKey);

        const tableData: ActivityLogTableDataType[] = groupActivities.map(
          (activity) => ({
            key: activity.id,
            time: format(activity.createdAt, 'HH:mm:ss'),
            user: activity.causer?.name ?? 'Sistem',
            action: activity.event,
            subject: `${
              logNameDisplayMap[activity.logName] || activity.logName
            } (ID: ${activity.subjectId})`
          })
        );

        return (
          <div key={dateKey} className={style.logGroup}>
            <div
              className={cx(style.groupHeader, isExpanded && style.expanded)}
              onClick={() => toggleGroup(dateKey)}
            >
              <div className={style.groupTitle}>
                {getGroupTitle(dateKey)}
                <span>{groupActivities.length} događaja</span>
              </div>
              <FaChevronRight className={style.groupToggleIcon} />
            </div>
            <div
              className={cx(style.tableContainer, isExpanded && style.visible)}
            >
              <Table
                className="cs-table-wrapper"
                columns={columns}
                dataSource={tableData}
                pagination={false}
                size="small"
              />
            </div>
          </div>
        );
      })}

      {isLoading && <div className={style.loading}>Učitavanje...</div>}
      {!isLoading && activities.length === 0 && (
        <div className={style.empty}>Nema aktivnosti za prikaz.</div>
      )}

      {canLoadMore && !isLoading && (
        <ButtonPrimary onClick={() => loadActivities()} className={style.btn}>
          Učitaj još
        </ButtonPrimary>
      )}
    </div>
  );
};

export default ActivityLogPage;
